import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export const Testimonials = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  const testimonials = [
    {
      name: t.testimonials.review1Name,
      role: t.testimonials.review1Role,
      image: 'https://images.unsplash.com/photo-1664101606938-e664f5852fac?w=100&h=100&fit=crop&facepad=2',
      quote: t.testimonials.review1Quote,
      rating: 5,
      highlight: t.testimonials.review1Stat,
    },
    {
      name: t.testimonials.review2Name,
      role: t.testimonials.review2Role,
      image: 'https://images.unsplash.com/photo-1706675289970-622e00b2ea1f?w=100&h=100&fit=crop&facepad=2',
      quote: t.testimonials.review2Quote,
      rating: 5,
      highlight: t.testimonials.review2Stat,
    },
    {
      name: t.testimonials.review3Name,
      role: t.testimonials.review3Role,
      image: 'https://images.unsplash.com/photo-1671450960874-0903baf942c5?w=100&h=100&fit=crop&facepad=2',
      quote: t.testimonials.review3Quote,
      rating: 5,
      highlight: t.testimonials.review3Stat,
    },
  ];

  return (
    <section 
      id="testimonials" 
      ref={sectionRef}
      className="py-20 md:py-32 relative"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#FFB703] text-sm font-medium uppercase tracking-wider mb-4 block">
            {t.testimonials.label}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold mb-4">
            {t.testimonials.title}
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            {t.testimonials.subtitle}
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="glass rounded-2xl p-5 md:p-8 relative group hover:border-white/20 transition-colors duration-300"
              data-testid={`testimonial-${index}`}
            >
              {/* Quote Icon - Hidden on mobile */}
              <Quote className="w-8 h-8 text-[#F59E0B]/30 absolute top-6 right-6 hidden md:block" />

              {/* Rating */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-[#FFB703] fill-[#FFB703]" />
                ))}
              </div>

              {/* Quote */}
              <p className="text-white/80 text-sm md:text-base leading-relaxed mb-6">
                &ldquo;{testimonial.quote}&rdquo;
              </p>

              {/* Author & Highlight - Stacked on mobile */}
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover border-2 border-white/10 flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <h4 className="text-white font-medium text-sm md:text-base truncate">{testimonial.name}</h4>
                    <p className="text-white/50 text-xs md:text-sm truncate">{testimonial.role}</p>
                  </div>
                </div>
                
                {/* Highlight Badge - Below author on mobile */}
                <div className="md:absolute md:bottom-6 md:right-6">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#10B981]/10 border border-[#10B981]/20 text-[#10B981] text-xs font-semibold">
                    {testimonial.highlight}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
